import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Check, X, Hand } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";

// Audio-only guest publisher: joins the stream channel with a second uid and publishes mic.
export function useMicSpeaker(streamId, myId, speakers, sendWs) {
  const ref = useRef({});
  const [live, setLive] = useState(false);
  const mine = speakers.find((s) => s.id === myId);
  const amSpeaker = Boolean(mine);

  useEffect(() => {
    let stopped = false;
    const start = async () => {
      try {
        const AgoraRTC = (await import("agora-rtc-sdk-ng")).default;
        AgoraRTC.setLogLevel(3);
        const { data: tok } = await api.post(`/streams/${streamId}/mic-token`);
        const track = await AgoraRTC.createMicrophoneAudioTrack();
        if (stopped) { track.close(); return; }
        const client = AgoraRTC.createClient({ mode: "live", codec: "vp8" });
        await client.setClientRole("host");
        await client.join(tok.app_id, tok.channel, tok.token, tok.uid);
        if (stopped) { track.close(); await client.leave().catch(() => {}); return; }
        await client.publish([track]);
        ref.current = { client, track };
        setLive(true);
        toast.success("You're on the mic! 🎙️");
      } catch (e) {
        console.error("Mic publish failed", e);
        toast.error(e?.name === "NotAllowedError" || e?.code === "PERMISSION_DENIED"
          ? "Microphone blocked — allow it in your browser settings"
          : "Could not join the mic");
        sendWs({ type: "mic_leave" });
      }
    };
    const stop = async () => {
      const { client, track } = ref.current;
      ref.current = {};
      setLive(false);
      try { track && track.close(); } catch (e) { /* ignore */ }
      try { client && (await client.leave()); } catch (e) { /* ignore */ }
    };
    if (amSpeaker && !ref.current.client && !ref.current.starting) {
      ref.current.starting = true;
      start().finally(() => { ref.current.starting = false; });
    } else if (!amSpeaker && ref.current.client) {
      stop();
    }
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amSpeaker, streamId]);

  // Host mute enforcement
  useEffect(() => {
    const t = ref.current.track;
    if (t && live) t.setMuted(Boolean(mine?.muted)).catch(() => {});
  }, [mine?.muted, live]);

  // Full teardown on unmount
  useEffect(() => () => {
    const { client, track } = ref.current;
    try { track && track.close(); } catch (e) { /* ignore */ }
    client && client.leave().catch(() => {});
    ref.current = {};
  }, []);

  return { amSpeaker, muted: Boolean(mine?.muted), live };
}

export function MicButton({ state, onClick }) {
  // state: idle | pending | live
  return (
    <button data-testid="mic-request-button" onClick={onClick}
      className={`glass-dark rounded-full w-11 h-11 shrink-0 flex items-center justify-center active:scale-90 transition-transform ${
        state === "live" ? "text-emerald-400 shadow-[0_0_14px_rgba(52,211,153,0.6)]"
          : state === "pending" ? "text-amber-300 animate-pulse" : "text-zinc-300"}`}>
      {state === "idle" ? <Hand size={17} /> : state === "pending" ? <Mic size={17} /> : <Mic size={17} />}
    </button>
  );
}

export function SpeakerChips({ speakers, isHost, onMute, onRemove }) {
  if (!speakers.length) return null;
  return (
    <div className="absolute bottom-[8.4rem] left-3 right-3 z-30 flex gap-2 overflow-x-auto no-scrollbar" data-testid="speaker-chips">
      {speakers.map((s) => (
        <div key={s.id} data-testid={`speaker-chip-${s.id}`}
          className="glass-dark rounded-full pl-1 pr-2 py-1 flex items-center gap-1.5 shrink-0 border border-emerald-400/30">
          <div className="relative">
            <img src={s.avatar} alt="" className="w-6 h-6 rounded-full object-cover border border-emerald-400/60" />
            <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center ${s.muted ? "bg-red-500" : "bg-emerald-500"}`}>
              {s.muted ? <MicOff size={8} className="text-white" /> : <Mic size={8} className="text-white" />}
            </span>
          </div>
          <span className="text-white text-[9px] font-bold max-w-[64px] truncate">{s.name}</span>
          {isHost && (
            <>
              <button data-testid={`speaker-mute-${s.id}`} onClick={() => onMute(s.id, !s.muted)}
                className={`w-5 h-5 rounded-full flex items-center justify-center ${s.muted ? "bg-red-500/30 text-red-300" : "bg-white/10 text-zinc-300"}`}>
                {s.muted ? <MicOff size={10} /> : <Mic size={10} />}
              </button>
              <button data-testid={`speaker-remove-${s.id}`} onClick={() => onRemove(s.id)}
                className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-zinc-300">
                <X size={10} />
              </button>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

export function MicRequestsSheet({ open, onClose, requests, onApprove, onDeny }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 z-[10000]" onClick={onClose} />
          <motion.div data-testid="mic-requests-sheet"
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            className="absolute bottom-0 left-0 right-0 z-[10001] glass-dark rounded-t-3xl bg-[#14141c]/95 max-h-[55%] flex flex-col pb-5">
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <p className="font-extrabold font-display text-sm flex items-center gap-1.5">
                <Mic size={15} className="text-emerald-400" /> Mic requests
              </p>
              <button data-testid="mic-sheet-close" onClick={onClose} className="text-zinc-400"><X size={18} /></button>
            </div>
            <p className="text-[10px] text-zinc-500 px-4 mb-2">Approve viewers to speak in your room — up to 4 at once 🎙️</p>
            <div className="overflow-y-auto px-3 space-y-2 flex-1">
              {requests.length === 0 && <p className="text-zinc-600 text-xs text-center py-6">No pending requests</p>}
              {requests.map((r) => (
                <div key={r.id} className="flex items-center justify-between bg-white/5 rounded-2xl px-3 py-2.5">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <img src={r.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
                    <div className="min-w-0">
                      <p className="text-white text-xs font-bold truncate">{r.name}</p>
                      <p className="text-zinc-500 text-[9px]">Lv.{r.level} · wants to speak</p>
                    </div>
                  </div>
                  <div className="flex gap-1.5 shrink-0 ml-2">
                    <button data-testid={`mic-approve-${r.id}`} onClick={() => onApprove(r.id)}
                      className="bg-emerald-500 text-white w-8 h-8 rounded-full flex items-center justify-center active:scale-90 transition-transform">
                      <Check size={14} />
                    </button>
                    <button data-testid={`mic-deny-${r.id}`} onClick={() => onDeny(r.id)}
                      className="bg-white/10 text-zinc-300 w-8 h-8 rounded-full flex items-center justify-center active:scale-90 transition-transform">
                      <X size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
